import { useEffect, useState } from 'react';
import { Sparkles, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

/**
 * iter877 — Storefront Preview Panel.
 *
 * Shown next to the Branch edit form. Fetches ONE representative menu item
 * that this branch currently sells and re-computes its customer-facing online
 * price locally as the admin edits the markup input — no round-trip needed.
 *
 * Price formula (matches backend public_ordering._serialise_menu_item):
 *   base = item.branch_prices[branchId] ?? item.price
 *   final = round(base * (1 + markupPercent/100), 2)
 */
export function StorefrontPreviewPanel({ branchId, markupPercent = 0, currencyCode = 'SAR' }) {
  const [sample, setSample] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!branchId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Prefer an item that has a branch_prices override for this branch;
        // else pick the first available one. Endpoint already returns
        // effective_price + price_source for us (iter877 injection).
        const { data } = await api.get(`/cashier/menu?branch_id=${branchId}`);
        const list = Array.isArray(data) ? data : [];
        const preferred = list.find(x => x.branch_prices && x.branch_prices[branchId] != null);
        setSample(preferred || list.find(x => x.is_available !== false) || null);
      } catch { setSample(null); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [branchId]);

  const overrideRaw = sample?.branch_prices?.[branchId];
  const hasOverride = overrideRaw != null && overrideRaw !== '';
  const base = hasOverride ? parseFloat(overrideRaw) : parseFloat(sample?.price || 0);
  const finalPrice = Math.round(base * (1 + (markupPercent || 0) / 100) * 100) / 100;

  return (
    <aside
      data-testid="storefront-preview-panel"
      className="hidden lg:flex flex-col gap-3 p-4 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/10 rounded-xl border border-orange-100 dark:border-orange-900/40"
    >
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-orange-500" />
        <h4 className="text-xs font-semibold tracking-wide uppercase text-orange-700 dark:text-orange-400">Live Storefront Preview</h4>
      </div>

      {!branchId && (
        <p className="text-[11px] text-muted-foreground">Save the branch once — a live preview will appear here.</p>
      )}

      {branchId && loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin" /> Loading sample item…</div>
      )}

      {branchId && !loading && !sample && (
        <p className="text-[11px] text-muted-foreground">No menu items sold at this branch yet. Add one to see the preview.</p>
      )}

      {branchId && !loading && sample && (
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            {sample.image_url ? (
              <img src={sample.image_url} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-orange-100 dark:bg-orange-950/40 flex items-center justify-center text-xl">🍽️</div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate" data-testid="preview-item-name">{sample.name}</p>
              <p className="text-[10px] text-muted-foreground line-clamp-2">{sample.description || sample.category}</p>
            </div>
          </div>

          <div className="space-y-1 text-[11px]">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Global price</span>
              <span className="tabular-nums">{parseFloat(sample.price || 0).toFixed(2)} {currencyCode}</span>
            </div>
            {hasOverride && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Branch override</span>
                <span className="tabular-nums text-orange-600 dark:text-orange-400 font-medium">{parseFloat(overrideRaw).toFixed(2)} {currencyCode}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Online markup</span>
              <span className="tabular-nums">{markupPercent || 0}%</span>
            </div>
          </div>

          <div className="pt-3 border-t border-orange-100 dark:border-orange-900/40" data-testid="preview-final-price-block">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Customer sees online</p>
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 tabular-nums" data-testid="preview-final-price">
              {finalPrice.toFixed(2)} <span className="text-sm">{currencyCode}</span>
            </p>
            {markupPercent > 0 && hasOverride && (
              <p className="text-[10px] text-muted-foreground mt-1">= {parseFloat(overrideRaw).toFixed(2)} × (1 + {markupPercent}%)</p>
            )}
            {/* iter878 — Warn on suspicious markups. Catches typos like
                "100" instead of "10", or a negative discount pattern. */}
            {(() => {
              const globalP = parseFloat(sample.price || 0);
              if (globalP <= 0) return null;
              const ratio = finalPrice / globalP;
              if (ratio > 2 || ratio < 0.5) {
                const kind = ratio > 2 ? 'above' : 'below';
                return (
                  <div
                    className="mt-3 flex items-start gap-2 p-2 rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/50"
                    data-testid="preview-warning-banner"
                  >
                    <AlertTriangle size={12} className="text-rose-600 dark:text-rose-400 mt-0.5 shrink-0" />
                    <p className="text-[10px] text-rose-700 dark:text-rose-300 leading-snug">
                      Customer price is {ratio.toFixed(1)}× {kind} the global price — did you mean a smaller number?
                    </p>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </div>
      )}
    </aside>
  );
}

export default StorefrontPreviewPanel;
